import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText,
  Target,
  ShoppingBag,
  CreditCard,
  Lock,
  Receipt,
  ShieldCheck,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { t } from "../contexts/translations";
import logo from "../assets/logo6.png";
import PaymentCards from "../components/PaymentCards";

export default function CGV({ language = "fr" }) {
  const merchant = {
    websiteName: "Palais Touristique",
    websiteUrl: "https://www.nouzha.ma",
    merchantName: "PALAIS TOURISTIQUE",
    postalAddress: "Place Atlas, 7 Rue Hassan Dkhissi, Fès 30000, Maroc",
    email: "Contact@palaistouristique.ma",
  };

  // Chaque section réutilise les mêmes clés de traduction (FR/EN) que la version
  // précédente : cgv<Key> pour le titre et cgv<Key>Content pour le contenu.
  const sections = [
    { key: "Preamble", icon: FileText },
    { key: "Purpose", icon: Target },
    { key: "Sales", icon: ShoppingBag },
    { key: "PaymentMethods", icon: CreditCard, highlight: true },
    { key: "Privacy", icon: Lock },
    { key: "Proof", icon: Receipt },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.06, duration: 0.5, ease: "easeOut" },
    }),
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 py-16 px-4 sm:px-6">
      {/* Décor lumineux */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-60">
        <div className="absolute -top-40 -left-32 h-96 w-96 rounded-full bg-orange-300/30 blur-[130px]" />
        <div className="absolute top-1/3 -right-40 h-[32rem] w-[32rem] rounded-full bg-amber-300/30 blur-[150px]" />
        <div className="absolute bottom-0 left-1/4 h-80 w-80 rounded-full bg-red-200/40 blur-[120px]" />
      </div>

      <div className="relative z-10 mx-auto max-w-4xl">
        {/* En-tête */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="mb-12 text-center"
        >
          <Link to="/" className="mb-6 inline-flex items-center">
            <img src={logo} alt={merchant.websiteName} className="h-16 w-auto drop-shadow-md" />
          </Link>

          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white/70 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-orange-600 shadow-sm backdrop-blur">
            <ShieldCheck className="h-4 w-4" />
            {language === "fr" ? "Sécurisé par CMI" : "Secured by CMI"}
          </div>

          <h1 className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-4xl font-black tracking-tight text-transparent sm:text-5xl">
            {t("cgvTitle", language)}
          </h1>

          <p className="mt-4 text-sm font-medium text-gray-500">
            <span className="font-bold text-gray-700">{merchant.websiteName}</span>
            <span className="mx-2 text-orange-300">•</span>
            <a
              href={merchant.websiteUrl}
              className="text-orange-600 transition-colors hover:text-orange-500"
            >
              {merchant.websiteUrl.replace(/^https?:\/\//, "")}
            </a>
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
          className="mb-8 rounded-3xl border border-orange-200/60 bg-white/80 p-8 shadow-xl shadow-orange-500/5 backdrop-blur-xl"
        >
          <p className="text-lg leading-relaxed text-gray-700">
            {language === "fr"
              ? "Nous vous invitons à lire ces conditions générales de vente en ligne du site web "
              : "We invite you to read these terms and conditions of online sales of "}
            <strong className="text-gray-900">{merchant.websiteName}</strong>.
          </p>
          <p className="mt-3 leading-relaxed text-gray-600">
            {language === "fr"
              ? "La validation de votre formulaire de transaction en ligne est considérée comme une acceptation irrévocable de ces conditions."
              : "The validation of your online transaction form is considered as an irrevocable acceptance of these terms."}
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => {
            const Icon = section.icon;
            const heading = t(`cgv${section.key}`, language);
            const content = t(`cgv${section.key}Content`, language);
            const paragraphs = content.split("\n\n");

            return (
              <motion.section
                key={section.key}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                className={`group rounded-3xl border p-8 shadow-xl backdrop-blur-xl transition-all duration-300 ${
                  section.highlight
                    ? "border-orange-300 bg-gradient-to-br from-white via-orange-50/80 to-amber-50/80 shadow-orange-500/10 ring-1 ring-orange-200"
                    : "border-orange-200/50 bg-white/80 shadow-orange-500/5 hover:border-orange-300/70"
                }`}
              >
                <div className="mb-5 flex items-center gap-4">
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-lg transition-transform duration-300 group-hover:scale-105 ${
                      section.highlight
                        ? "bg-gradient-to-br from-orange-500 to-red-500 shadow-orange-500/30"
                        : "bg-gradient-to-br from-amber-400 to-orange-500 shadow-orange-500/20"
                    }`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <h2 className="text-xl font-extrabold tracking-tight text-gray-900 sm:text-2xl">
                    {heading}
                  </h2>
                </div>

                <div className="space-y-4 pl-1">
                  {paragraphs.map((paragraph, i) => (
                    <p key={i} className="leading-relaxed text-gray-600">
                      {paragraph}
                    </p>
                  ))}
                </div>

                {section.highlight && (
                  <div className="mt-6 flex flex-col items-center justify-between gap-4 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 px-6 py-4 sm:flex-row">
                    <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-white/70">
                      <Lock className="h-4 w-4 text-orange-400" />
                      {language === "fr"
                        ? "Cartes acceptées — Paiement 100% sécurisé"
                        : "Accepted cards — 100% secure payment"}
                    </span>
                    <PaymentCards language={language} showLabel={false} />
                  </div>
                )}
              </motion.section>
            );
          })}
        </div>

        {/* Navigation bas de page */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mt-10 flex flex-col items-center justify-between gap-4 rounded-3xl border border-orange-200/60 bg-white/70 px-8 py-6 shadow-lg backdrop-blur-xl sm:flex-row"
        >
          <Link
            to="/paiement-securise"
            className="group inline-flex items-center gap-2 font-bold text-orange-600 transition-colors hover:text-orange-500"
          >
            {language === "fr" ? "Page « Paiement sécurisé »" : "'Secure Payment' page"}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/"
            className="group inline-flex items-center gap-2 font-medium text-gray-500 transition-colors hover:text-gray-800"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            {language === "fr" ? "Retour à l’accueil" : "Back to home"}
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
